/**
 * The "today" column of a kind's row and the line under it: the cell says
 * how many of today's real drops are of that kind — "none", or "—" when no
 * day is laid out yet — and the line draws each one as a mini tile (its
 * time, the lock word when it may not be touched; the mix and the ranks in
 * the hover), capped with "+N". Re-animates whenever the drops' mixes change.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenDropKind } from '@trafficpulse/shared';

import { DropTile } from './DropTile';
import type { TodayDrop } from './kind-facts';

/** Tiles shown before "+N" — a kind rarely has more drops a day. */
export const TODAY_TILES_MAX = 12;

interface Props {
  kind: DropGenDropKind;
  /** Today's drops of this kind; null = no day laid out yet. */
  drops: TodayDrop[] | null;
}

export const TodayCell: FC<Props> = ({ kind, drops }) => {
  if (drops === null) {
    return (
      <span className="text-[11px] text-muted-foreground" title="no day laid out yet">
        —
      </span>
    );
  }
  if (drops.length === 0) return <span className="text-[11px] text-muted-foreground">none</span>;
  return (
    <span className="font-mono text-xs font-semibold tabular-nums" data-testid={`today-${kind}`}>
      {drops.length}
    </span>
  );
};

/** The mini tiles of today's drops of one kind, in time order. */
export const TodayTiles: FC<{ kind: DropGenDropKind; drops: TodayDrop[] }> = ({ kind, drops }) => {
  const signature = drops.map((d) => (d.locked ? 'L' : `${d.mix.proven}/${d.mix.ai}`)).join(',');
  const rest = drops.length - TODAY_TILES_MAX;
  return (
    <span key={signature} className="inline-flex flex-wrap items-center gap-1" aria-label={`today's ${kind} drops`}>
      {drops.slice(0, TODAY_TILES_MAX).map((d, i) => (
        <DropTile key={`${d.minute}-${d.containerName}-${i}`} drop={d} index={i} mini />
      ))}
      {rest > 0 ? <span className="text-[10px] text-muted-foreground">+{rest}</span> : null}
    </span>
  );
};
